import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { clearAuth0Cache, getRedirectUri } from '../utils/auth0-session';
import { usePharmacyStore } from '../stores/pharmacyStore';
import { queryClient } from '../providers/QueryProvider';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  permissions: string[];
  phoneNumber?: string;
  employeeId?: string;
}

export interface CustomRoleData {
  customRole: string;
  permissions: Record<string, boolean>;
  isActive: boolean;
}

// Track sync status globally to prevent duplicate calls
let syncInProgress = false;
let lastSyncUserId: string | undefined | null = null;
let lastSyncTime = 0;
const SYNC_COOLDOWN = 60000; // 1 minute cooldown between syncs for the same user

// Custom-role fetch dedup + cache, shared across ALL useAuth() consumers.
// Previously every mounted useAuth() (ProtectedRoute + Topbar + Sidebar + page
// = ~4 per page) ran its own GET /api/user-management/user/:id?pharmacyId=...,
// uncached — so the role lookup fanned out on every navigation. This collapses
// concurrent callers onto one in-flight request and serves a short-lived cache
// on re-mount, mirroring the sync-dedup pattern above.
interface CustomRoleCacheEntry {
  data: CustomRoleData | null;
  ts: number;
}
const customRoleCache = new Map<string, CustomRoleCacheEntry>();
const customRoleInflight = new Map<string, Promise<CustomRoleData | null>>();
const CUSTOM_ROLE_TTL = 120000; // 2 min — matches the app's default React Query staleTime

/** Clear the custom-role cache (e.g. on logout, so a new user never sees a stale role). */
export const clearCustomRoleCache = () => {
  customRoleCache.clear();
  customRoleInflight.clear();
};

async function loadCustomRole(
  userId: string,
  pharmacyId: string,
): Promise<CustomRoleData | null> {
  const key = `${userId}:${pharmacyId}`;

  const cached = customRoleCache.get(key);
  if (cached && Date.now() - cached.ts < CUSTOM_ROLE_TTL) {
    return cached.data;
  }

  const inflight = customRoleInflight.get(key);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      const response = await api.get(
        `/api/user-management/user/${userId}?pharmacyId=${pharmacyId}`,
      );
      const data: CustomRoleData | null =
        response.data && response.data.customRole
          ? {
              customRole: response.data.customRole,
              permissions: response.data.permissions || {},
              isActive: response.data.isActive ?? true,
            }
          : null;
      customRoleCache.set(key, { data, ts: Date.now() });
      return data;
    } finally {
      // Errors are NOT cached — the next mount retries.
      customRoleInflight.delete(key);
    }
  })();

  customRoleInflight.set(key, promise);
  return promise;
}

export const useAuth = () => {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const { activePharmacy } = usePharmacyStore();
  const [customRole, setCustomRole] = useState<CustomRoleData | null>(null);
  // Initialize as `true` so the first render reports "still loading" until the
  // fetch effect has had a chance to run. Otherwise pages that gate on
  // `isLoading` (e.g. SMS Templates admin) flash `!loading && !hasRole` for
  // one render and redirect away before the custom-role API call returns.
  const [customRoleLoading, setCustomRoleLoading] = useState(true);

  // Sync user with backend when authenticated (with deduplication)
  useEffect(() => {
    const syncUser = async () => {
      if (isAuthenticated && auth0User) {
        const userId = auth0User.sub;
        const now = Date.now();

        // Skip if already syncing or if same user was synced recently
        if (syncInProgress || (userId === lastSyncUserId && now - lastSyncTime < SYNC_COOLDOWN)) {
          return;
        }

        syncInProgress = true;

        try {
          // Include user info from ID token in the request body
          const syncData = {
            email: auth0User.email,
            emailVerified: auth0User.email_verified,
            name: auth0User.name,
            picture: auth0User.picture,
            sub: auth0User.sub,
          };

          await api.post('/api/auth/sync', syncData);

          // Update sync tracking
          lastSyncUserId = userId;
          lastSyncTime = now;
        } catch (error) {
          console.error('Failed to sync user:', error);
        } finally {
          syncInProgress = false;
        }
      }
    };

    syncUser();
  }, [isAuthenticated, auth0User]);

  // Fetch user's custom role from database when pharmacy context is available.
  // Goes through loadCustomRole() so concurrent useAuth() consumers share one
  // request + a short-lived cache instead of each firing its own GET.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!isAuthenticated || !auth0User || !activePharmacy?.id) {
        setCustomRole(null);
        setCustomRoleLoading(false);
        return;
      }

      setCustomRoleLoading(true);
      try {
        const userId = auth0User.sub?.replace('auth0|', '');
        const data = await loadCustomRole(userId as string, activePharmacy.id);
        if (!cancelled) setCustomRole(data);
      } catch (error) {
        console.error('Failed to fetch custom role:', error);
        if (!cancelled) setCustomRole(null);
      } finally {
        if (!cancelled) setCustomRoleLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, auth0User, activePharmacy?.id]);

  const login = useCallback(
    (forceClean = false) => {
      if (forceClean) {
        // Clear all Auth0 cache before login
        clearAuth0Cache();
      }

      const redirectUri = getRedirectUri();
      console.log('Login initiated with redirect URI:', redirectUri);

      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
        authorizationParams: {
          redirect_uri: redirectUri,
          // Force interactive login
          prompt: forceClean ? 'login' : undefined,
        },
      });
    },
    [loginWithRedirect],
  );

  const logoutUser = useCallback(() => {
    // Clear the shared custom-role cache so the next user to log in on this
    // device never reads the previous user's cached role/permissions.
    clearCustomRoleCache();

    // PRX-87: drop every cached query (the recent-patients history among them)
    // before the redirect, so no sign-out path can hand one user's cached
    // lists to the next person at this workstation.
    queryClient.clear();

    // Clear pharmacy-related localStorage data
    localStorage.removeItem('pharmacy-store');
    localStorage.removeItem('recentPharmacies');
    localStorage.removeItem('sidebarHidden');
    localStorage.removeItem('sidebarCollapsed');

    // Clear any zustand persist storage
    const stores = ['pharmacy-store', 'pharmacy-selector'];
    stores.forEach((storeName) => {
      const keys = Object.keys(localStorage).filter((key) => key.includes(storeName));
      keys.forEach((key) => localStorage.removeItem(key));
    });

    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const hasRole = useCallback(
    (role: string) => {
      const appMetadata = auth0User?.['https://phox.pharmaguide.com/app_metadata'];
      const roles = appMetadata?.roles || auth0User?.['https://api.pharmaguide.ca/roles'] || [];
      return roles.includes(role);
    },
    [auth0User],
  );

  const hasPermission = useCallback(
    (permission: string) => {
      const permissions = auth0User?.['https://api.pharmaguide.ca/permissions'] || [];
      return permissions.includes(permission);
    },
    [auth0User],
  );

  const getProfile = useCallback(async (): Promise<User | null> => {
    if (!isAuthenticated) return null;

    try {
      const response = await api.get<User>('/api/auth/profile');
      return response.data;
    } catch (error) {
      console.error('Failed to get profile:', error);
      return null;
    }
  }, [isAuthenticated]);

  // NEW: Check if user has specific custom role (database-backed)
  const hasCustomRole = useCallback(
    (role: string): boolean => {
      const hasRole = customRole?.customRole === role && customRole?.isActive === true;

      return hasRole;
    },
    [customRole],
  );

  // NEW: Check if user has specific permission (database-backed)
  const hasCustomPermission = useCallback(
    (permission: string): boolean => {
      if (!customRole?.permissions) return false;
      return customRole.permissions[permission] === true;
    },
    [customRole],
  );

  // NEW: Check if user is kiosk-only (should be redirected to /kiosk)
  const isKioskOnly = useCallback((): boolean => {
    return hasCustomRole('kiosk');
  }, [hasCustomRole]);

  return {
    isAuthenticated,
    isLoading: isLoading || customRoleLoading,
    user: auth0User,
    login,
    logout: logoutUser,
    hasRole, // Auth0 roles (legacy, kept for backward compatibility)
    hasPermission, // Auth0 permissions (legacy, kept for backward compatibility)
    hasCustomRole, // Database custom roles (NEW)
    hasCustomPermission, // Database custom permissions (NEW)
    isKioskOnly, // Kiosk user check (NEW)
    customRole, // Full custom role object (NEW)
    getProfile,
    getAccessTokenSilently,
  };
};
